import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Button, Badge, Spinner, Alert,
    Form, InputGroup, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";

export default function Echeancier() {
    const navigate = useNavigate();
    const [echeances, setEcheances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    
    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchEcheances();
        fetchStats();
    }, []);

    const fetchEcheances = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/reglements.php?action=echeancier',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setEcheances(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/reglements.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const getDelaiBadge = (jours) => {
        if (jours < 0) {
            return <Badge bg="danger">En retard ({Math.abs(jours)}j)</Badge>;
        } else if (jours <= 7) {
            return <Badge bg="warning">Échéance proche ({jours}j)</Badge>;
        } else {
            return <Badge bg="info">Dans {jours} jours</Badge>;
        }
    };

    const getStatutFactureBadge = (statut) => {
        const configs = {
            emise: { bg: 'primary', label: 'Émise' },
            payee_partielle: { bg: 'warning', label: 'Partiel...' },
            payee: { bg: 'success', label: 'Payée' },
            impayee: { bg: 'danger', label: 'Impayée' }
        };
        const config = configs[statut] || { bg: 'secondary', label: statut };
        return <Badge bg={config.bg}>{config.label}</Badge>;
    };

    const filteredEcheances = echeances.filter(e => 
        e.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
        e.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
        e.code_client?.toLowerCase().includes(filterText.toLowerCase())
    );

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredEcheances.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredEcheances.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const totalImpayes = filteredEcheances.reduce((sum, e) => sum + parseFloat(e.solde || 0), 0);

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center">
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchEcheances}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h1 className="page-title mb-1">Échéancier</h1>
                                    <p className="text-muted">Suivi des factures impayées et échéances</p>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/ventes/reglements')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour aux règlements
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Résumé */}
                    <Row className="g-3 mb-4">
                        {/* Carte Total impayés */}
                        <Col md={4}>
                            <Card className="border-0 shadow h-100" style={{
                                borderRadius: '12px',
                                borderLeft: '5px solid #dc3545',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-error-warning-line text-danger fs-1"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted text-uppercase fw-semibold">Total impayés</small>
                                            <h5 className="mb-0 fw-bold text-danger">{formatCurrency(totalImpayes)}</h5>
                                            <small className="text-muted">{filteredEcheances.length} facture(s)</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Carte Échéances critiques */}
                        <Col md={4}>
                            <Card className="border-0 shadow h-100" style={{
                                borderRadius: '12px',
                                borderLeft: '5px solid #ffc107',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-time-line text-warning fs-1"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted text-uppercase fw-semibold">Échéances critiques</small>
                                            <h5 className="mb-0 fw-bold text-warning">
                                                {filteredEcheances.filter(e => e.jours_restant <= 7 && e.jours_restant >= 0).length}
                                            </h5>
                                            <small className="text-muted">≤ 7 jours</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Carte En retard */}
                        <Col md={4}>
                            <Card className="border-0 shadow h-100" style={{
                                borderRadius: '12px',
                                borderLeft: '5px solid #0dcaf0',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-info bg-opacity-10 p-3 rounded-3 me-3">
                                            <i className="ri-alert-line text-info fs-1"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted text-uppercase fw-semibold">En retard</small>
                                            <h5 className="mb-0 fw-bold text-info">
                                                {filteredEcheances.filter(e => e.jours_restant < 0).length}
                                            </h5>
                                            <small className="text-muted">Action immédiate</small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Recherche */}
                    <Card className="border-0 mb-4">
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <InputGroup>
                                        <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher par facture, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des échéances avec pagination */}
                    <Card className="border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>N° Facture</th>
                                            <th>Date facture</th>
                                            <th>Client</th>
                                            <th>Échéance</th>
                                            <th>Montant TTC</th>
                                            <th>Réglé</th>
                                            {/* <th>Solde</th> */}
                                            <th>Délai</th>
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((facture) => (
                                                <tr key={facture.id_facture} className={facture.jours_restant < 0 ? 'bg-danger bg-opacity-10' : ''}>
                                                    <td>
                                                        <Link to={`/ventes/facturesDetails/${facture.id_facture}`} className="text-primary" title={facture.numero_facture}>
                                                            {facture.numero_facture?.length > 6 
                                                                ? facture.numero_facture.substring(0, 6) + '...' 
                                                                : facture.numero_facture}
                                                        </Link>
                                                    </td>
                                                    <td>{formatDate(facture.date_facture)}</td>
                                                    <td>
                                                        <strong>{facture.client_nom}</strong>
                                                        <div><small className="text-muted">{facture.code_client}</small></div>
                                                    </td>
                                                    <td>
                                                        <strong className={facture.jours_restant < 0 ? 'text-danger' : ''}>
                                                            {formatDate(facture.date_echeance)}
                                                        </strong>
                                                    </td>
                                                    <td className="fw-bold">{formatCurrency(facture.montant_ttc)}</td>
                                                    <td className="text-success">{formatCurrency(facture.montant_regle)}</td>
                                                    {/* <td className="fw-bold text-primary">{formatCurrency(facture.solde)}</td> */}
                                                    <td>{getDelaiBadge(facture.jours_restant)}</td>
                                                    <td>{getStatutFactureBadge(facture.statut)}</td>
                                                    <td className="text-center">
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => navigate(`/ventes/NouveauReglement/?facture=${facture.id_facture}&client=${facture.id_client}`, { 
                                                                state: { 
                                                                    clientId: facture.id_client,
                                                                    factureId: facture.id_facture,
                                                                    montantMax: facture.solde
                                                                } 
                                                            })}
                                                        >
                                                            <i className="ri-bank-card-line me-1"></i>Saisir règlement
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <i className="ri-calendar-check-line fs-1 text-muted mb-3 d-block"></i>
                                                    <h5>Aucune échéance en cours</h5>
                                                    <p className="text-muted">Toutes les factures sont soldées</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredEcheances.length)} sur {filteredEcheances.length} échéances
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>
        </>
    );
}    